import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { subscriptionAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

// All feature modules for comparison
const allModules = [
  { key: 'assets', label: 'Assets', type: 'limit' },
  { key: 'users', label: 'Team Members', type: 'limit' },
  { key: 'retention_days', label: 'Data Retention', type: 'limit', suffix: ' days' },
  { key: 'analytics', label: 'Analytics Dashboard', type: 'feature' },
  { key: 'monitoring', label: 'Website Monitoring', type: 'feature' },
  { key: 'security', label: 'Cybersecurity Auditing', type: 'feature' },
  { key: 'server_monitoring', label: 'Server Monitoring', type: 'feature' },
  { key: 'ai_advisor', label: 'Intelligent Security Monitoring', type: 'feature' },
  { key: 'reports', label: 'Reports & Exports', type: 'feature' },
  { key: 'alerts', label: 'Alerts & Notifications', type: 'feature' },
  { key: 'event_points', label: 'Register Events & Conversion Tracking', type: 'feature' },
  { key: 'user_permissions', label: 'User Permissions & Roles', type: 'feature' },
  { key: 'seo', label: 'SEO Scanner & Summary', type: 'feature' },
  { key: 'seo_ai', label: 'AI SEO Optimizer', type: 'feature' },
  { key: 'seo_rank_tracking', label: 'Keyword Rank Tracking', type: 'feature' },
  { key: 'feedback', label: 'Customer Feedback Widget', type: 'feature' },
  { key: 'email_monitoring', label: 'Email Security (SPF, DKIM, DMARC, blacklists)', type: 'feature' },
  { key: 'endpoint_management', label: 'Laptop & Desktop Protection', type: 'feature' },
  { key: 'api_access', label: 'API Access', type: 'feature' },
  { key: 'priority_support', label: 'Priority Support', type: 'feature' },
];

const planColors: Record<string, string> = {
  free: '#6c757d',
  starter: '#0d6efd',
  professional: '#6f42c1',
  enterprise: '#dc3545',
};

const planIcons: Record<string, string> = {
  free: 'bi-gift',
  starter: 'bi-rocket-takeoff',
  professional: 'bi-star-fill',
  enterprise: 'bi-building',
};

const PlansPage: React.FC = () => {
  useDocumentTitle('Subscription Plans');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<any[]>([]);
  const [currentSub, setCurrentSub] = useState<any>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [upsellPlan, setUpsellPlan] = useState<any>(null);
  const [userCurrency, setUserCurrency] = useState('USD');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, currentRes] = await Promise.all([
          subscriptionAPI.listPlans(),
          subscriptionAPI.getCurrent().catch(() => ({ data: { data: null } })),
        ]);
        const plansData = plansRes.data.data;
        const plansArray = Array.isArray(plansData) ? plansData : (plansData?.plans || []);
        const currency = Array.isArray(plansData) ? 'USD' : (plansData?.currency || 'USD');
        setPlans(plansArray);
        setUserCurrency(currency);
        setCurrentSub(currentRes.data.data?.subscription);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectPlan = (plan: any) => {
    if (currentSub?.plan_id === plan.id) return;

    if (plan.price_monthly <= 0) {
      if (window.confirm('Downgrade to Free plan? Your current subscription will be canceled.')) {
        subscriptionAPI.changePlan(plan.id).then(() => {
          toast.success('Downgraded to Free plan');
          navigate('/billing');
        }).catch((err: any) => {
          toast.error(err.response?.data?.message || 'Failed to change plan');
        });
      }
      return;
    }

    if (currentSub && currentSub.amount > 0) {
      if (window.confirm(`Switch to ${plan.name} plan?`)) {
        subscriptionAPI.changePlan(plan.id).then(() => {
          toast.success(`Switched to ${plan.name} plan`);
          navigate('/billing');
        }).catch((err: any) => {
          toast.error(err.response?.data?.message || 'Failed to change plan');
        });
      }
      return;
    }

    // Show yearly upsell popup when selecting monthly and yearly saves money
    if (billingPeriod === 'monthly') {
      const yearlyPrice = Number(plan.price_yearly) || 0;
      const monthlyTotal = Number(plan.price_monthly) * 12;
      const savings = monthlyTotal - yearlyPrice;
      if (yearlyPrice > 0 && savings > 0) {
        setUpsellPlan(plan);
        return;
      }
    }

    navigate(`/billing/checkout?plan=${plan.id}&period=${billingPeriod}`);
  };

  const getFeatureValue = (plan: any, module: typeof allModules[0]) => {
    const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || {});
    const limits = typeof plan.limits === 'string' ? JSON.parse(plan.limits) : (plan.limits || {});

    if (module.type === 'limit') {
      const val = limits[module.key];
      if (val === -1 || val === undefined) return 'Unlimited';
      return `${val}${module.suffix || ''}`;
    }
    return features[module.key] ?? false;
  };

  const getPreviousPlanName = (index: number) => {
    if (index <= 0) return null;
    return plans[index - 1]?.name;
  };

  if (loading) {
    return <DashboardLayout><div className="d-flex justify-content-center py-5"><LoadingSpinner /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="container-fluid px-0">
        {/* Header */}
        <div className="text-center mb-5">
          <button className="btn btn-link text-decoration-none mb-2" onClick={() => navigate('/billing')}>
            <i className="bi bi-arrow-left me-2"></i>Back to Billing
          </button>
          <h2 className="fw-bold mb-2" style={{ fontSize: '2rem' }}>Choose the Right Plan for Your Business</h2>
          <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
            Start free and scale as you grow. All plans include core analytics.
          </p>

          <div className="d-inline-flex align-items-center bg-light rounded-pill p-1 mb-4">
            <button
              className={`btn btn-sm rounded-pill px-4 ${billingPeriod === 'monthly' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setBillingPeriod('monthly')}
            >Monthly</button>
            <button
              className={`btn btn-sm rounded-pill px-4 ${billingPeriod === 'yearly' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>Save 17%</span>
            </button>
          </div>
        </div>

        {plans.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox text-muted" style={{ fontSize: '3rem' }}></i>
            <p className="text-muted mt-3">No plans available at the moment. Please check back later.</p>
          </div>
        ) : (
          <>
            {/* Plan Cards */}
            <div className="row g-4 mb-5 justify-content-center">
              {plans.map((plan: any, index: number) => {
                const price = Number(billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
                const monthlyEquiv = billingPeriod === 'yearly' ? (Number(plan.price_yearly) / 12) : price;
                const isCurrent = currentSub?.plan_id === plan.id;
                const features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || {});
                const limits = typeof plan.limits === 'string' ? JSON.parse(plan.limits) : (plan.limits || {});
                const slug = plan.slug || 'free';
                const color = planColors[slug] || '#0d6efd';
                const icon = planIcons[slug] || 'bi-box';
                const isPopular = slug === 'professional';
                const prevPlan = getPreviousPlanName(index);

                return (
                  <div key={plan.id} className="col-xl-3 col-lg-6 col-md-6">
                    <div
                      className={`card h-100 border-0 shadow ${isCurrent ? 'border-2' : ''} ${isPopular ? 'shadow-lg' : ''}`}
                      style={{
                        borderColor: isCurrent ? color : 'transparent',
                        borderWidth: isCurrent ? '2px' : '1px',
                        borderStyle: 'solid',
                        transform: isPopular ? 'scale(1.02)' : 'none',
                        zIndex: isPopular ? 1 : 0,
                      }}
                    >
                      {/* Top badge */}
                      {isPopular && (
                        <div className="text-center py-2 fw-bold text-white" style={{ backgroundColor: color, borderRadius: '0.375rem 0.375rem 0 0', fontSize: '0.8rem' }}>
                          MOST POPULAR
                        </div>
                      )}
                      {isCurrent && !isPopular && (
                        <div className="text-center py-2 fw-bold text-white" style={{ backgroundColor: color, borderRadius: '0.375rem 0.375rem 0 0', fontSize: '0.8rem' }}>
                          CURRENT PLAN
                        </div>
                      )}

                      <div className="card-body p-4 d-flex flex-column">
                        {/* Plan Icon & Name */}
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: '48px', height: '48px', backgroundColor: `${color}15` }}>
                            <i className={`bi ${icon}`} style={{ fontSize: '1.5rem', color }}></i>
                          </div>
                          <div>
                            <h4 className="fw-bold mb-0">{plan.name}</h4>
                            {plan.description && <small className="text-muted">{plan.description}</small>}
                          </div>
                        </div>

                        {/* Price */}
                        <div className="mb-3">
                          {price <= 0 ? (
                            <div>
                              <span className="fw-bold" style={{ fontSize: '2.5rem', color }}>Free</span>
                              <div className="text-muted small">Forever</div>
                            </div>
                          ) : (
                            <div>
                              <span className="fw-bold" style={{ fontSize: '2.5rem', color }}>
                                {userCurrency} {billingPeriod === 'yearly' ? monthlyEquiv.toFixed(0) : price.toFixed(0)}
                              </span>
                              <span className="text-muted">/month</span>
                              {billingPeriod === 'yearly' && (
                                <div className="text-muted small">Billed {userCurrency} {price.toFixed(0)}/year</div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Inheritance line */}
                        {prevPlan && (
                          <div className="mb-3 pb-3 border-bottom">
                            <span className="fw-semibold" style={{ color, fontSize: '0.85rem' }}>
                              <i className="bi bi-plus-circle me-1"></i>
                              Everything in {prevPlan}, plus:
                            </span>
                          </div>
                        )}

                        {/* Key Features */}
                        <ul className="list-unstyled mb-4 flex-grow-1">
                          <li className="mb-2">
                            <i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>
                            <strong>{limits?.assets === -1 ? 'Unlimited' : limits?.assets || 1}</strong> Asset{(limits?.assets !== 1 && limits?.assets !== -1) ? 's' : ''}
                          </li>
                          <li className="mb-2">
                            <i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>
                            <strong>{limits?.users === -1 ? 'Unlimited' : limits?.users || 1}</strong> Team Member{(limits?.users !== 1 && limits?.users !== -1) ? 's' : ''}
                          </li>
                          <li className="mb-2">
                            <i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>
                            <strong>{limits?.retention_days || 30}</strong> days data retention
                          </li>
                          {features?.analytics && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Analytics Dashboard</li>
                          )}
                          {features?.monitoring && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Website Monitoring</li>
                          )}
                          {features?.security && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Cybersecurity Auditing</li>
                          )}
                          {features?.server_monitoring && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Server Monitoring</li>
                          )}
                          {features?.ai_advisor && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Intelligent Security Monitoring</li>
                          )}
                          {features?.reports && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Reports & Exports</li>
                          )}
                          {features?.alerts && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Alerts & Notifications</li>
                          )}
                          {features?.user_permissions && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>User Permissions & Roles</li>
                          )}
                          {features?.seo && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>SEO Scanner & Summary</li>
                          )}
                          {features?.seo_ai && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>AI SEO Optimizer</li>
                          )}
                          {features?.api_access && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>API Access</li>
                          )}
                          {features?.priority_support && (
                            <li className="mb-2"><i className="bi bi-check-lg me-2 fw-bold" style={{ color }}></i>Priority Support</li>
                          )}
                        </ul>

                        {/* CTA Button */}
                        <button
                          className={`btn w-100 py-2 fw-semibold ${isCurrent ? 'btn-outline-secondary disabled' : ''}`}
                          style={!isCurrent ? { backgroundColor: color, borderColor: color, color: '#fff' } : {}}
                          onClick={() => handleSelectPlan(plan)}
                          disabled={isCurrent}
                        >
                          {isCurrent ? 'Current Plan' : price <= 0 ? 'Get Started Free' : currentSub && currentSub.amount > 0 ? 'Switch Plan' : 'Subscribe Now'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Feature Comparison Table */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0 fw-bold">Feature Comparison</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ tableLayout: 'fixed' }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '30%' }}>Feature</th>
                        {plans.map((plan: any) => {
                          const slug = plan.slug || 'free';
                          const color = planColors[slug] || '#0d6efd';
                          const isCurrent = currentSub?.plan_id === plan.id;
                          return (
                            <th key={plan.id} className="text-center" style={{ color: isCurrent ? color : undefined }}>
                              {plan.name}
                              {isCurrent && <div><small className="badge bg-primary">Current</small></div>}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {allModules.map((module) => (
                        <tr key={module.key}>
                          <td className="fw-medium">{module.label}</td>
                          {plans.map((plan: any) => {
                            const val = getFeatureValue(plan, module);
                            return (
                              <td key={plan.id} className="text-center">
                                {typeof val === 'boolean' ? (
                                  val ? (
                                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.2rem' }}></i>
                                  ) : (
                                    <i className="bi bi-x-circle text-muted" style={{ fontSize: '1.2rem', opacity: 0.3 }}></i>
                                  )
                                ) : (
                                  <span className="fw-semibold">{val}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {/* Price Row */}
                      <tr className="table-light">
                        <td className="fw-bold">Price</td>
                        {plans.map((plan: any) => {
                          const price = Number(billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
                          return (
                            <td key={plan.id} className="text-center fw-bold">
                              {price <= 0 ? 'Free' : `${userCurrency} ${price.toFixed(0)}/${billingPeriod === 'yearly' ? 'yr' : 'mo'}`}
                            </td>
                          );
                        })}
                      </tr>
                      {/* Action Row */}
                      <tr>
                        <td></td>
                        {plans.map((plan: any) => {
                          const price = Number(billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
                          const isCurrent = currentSub?.plan_id === plan.id;
                          const slug = plan.slug || 'free';
                          const color = planColors[slug] || '#0d6efd';
                          return (
                            <td key={plan.id} className="text-center">
                              <button
                                className={`btn btn-sm ${isCurrent ? 'btn-outline-secondary disabled' : ''}`}
                                style={!isCurrent ? { backgroundColor: color, borderColor: color, color: '#fff' } : {}}
                                onClick={() => handleSelectPlan(plan)}
                                disabled={isCurrent}
                              >
                                {isCurrent ? 'Current' : price <= 0 ? 'Get Started' : 'Select'}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* FAQ / Info */}
            <div className="text-center py-4 text-muted">
              <p className="small mb-1">All plans can be upgraded or downgraded at any time.</p>
              <p className="small mb-0">Need a custom plan? <a href="mailto:info@cloudayn.com">Contact us</a></p>
            </div>
          </>
        )}
      </div>

      {/* Yearly Upsell Modal */}
      {upsellPlan && (() => {
        const monthlyPrice = Number(upsellPlan.price_monthly);
        const yearlyPrice = Number(upsellPlan.price_yearly);
        const monthlyTotal = monthlyPrice * 12;
        const savings = monthlyTotal - yearlyPrice;
        const savingsPct = Math.round((savings / monthlyTotal) * 100);
        const slug = upsellPlan.slug || 'free';
        const color = planColors[slug] || '#0d6efd';
        return (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', position: 'fixed', inset: 0, zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '480px' }}>
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header border-0 pb-0">
                  <button type="button" className="btn-close" onClick={() => setUpsellPlan(null)}></button>
                </div>
                <div className="modal-body text-center px-4 pb-4">
                  <div className="mb-3">
                    <span className="badge rounded-pill px-3 py-2 fw-semibold" style={{ backgroundColor: `${color}20`, color }}>
                      Save {savingsPct}% with Yearly
                    </span>
                  </div>
                  <h4 className="fw-bold mb-2">Get a better deal!</h4>
                  <p className="text-muted mb-4">
                    Switch to yearly billing and save <strong>{userCurrency} {savings.toFixed(0)}</strong> a year on the <strong>{upsellPlan.name}</strong> plan.
                  </p>

                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <div className="rounded p-3 bg-light text-center">
                        <div className="text-muted small mb-1">Monthly billing</div>
                        <div className="fw-bold fs-5">{userCurrency} {monthlyPrice.toFixed(0)}<span className="fs-6 text-muted">/mo</span></div>
                        <div className="text-muted small">{userCurrency} {monthlyTotal.toFixed(0)}/year</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="rounded p-3 text-center border-2" style={{ backgroundColor: `${color}08`, borderColor: color, borderStyle: 'solid' }}>
                        <div className="small mb-1 fw-semibold" style={{ color }}>Yearly billing</div>
                        <div className="fw-bold fs-5" style={{ color }}>{userCurrency} {(yearlyPrice / 12).toFixed(0)}<span className="fs-6 text-muted">/mo</span></div>
                        <div className="small" style={{ color }}>{userCurrency} {yearlyPrice.toFixed(0)}/year</div>
                      </div>
                    </div>
                  </div>

                  <button
                    className="btn btn-lg w-100 fw-semibold mb-2"
                    style={{ backgroundColor: color, borderColor: color, color: '#fff' }}
                    onClick={() => { setUpsellPlan(null); navigate(`/billing/checkout?plan=${upsellPlan.id}&period=yearly`); }}
                  >
                    <i className="bi bi-star-fill me-2"></i>Switch to Yearly — Save {userCurrency} {savings.toFixed(0)}
                  </button>
                  <button
                    className="btn btn-outline-secondary w-100"
                    onClick={() => { setUpsellPlan(null); navigate(`/billing/checkout?plan=${upsellPlan.id}&period=monthly`); }}
                  >
                    Continue with Monthly
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </DashboardLayout>
  );
};

export default PlansPage;
